import React, { useState } from 'react';
import {
  Create,
  SimpleForm,
  TextInput,
  NumberInput,
  BooleanInput,
  SelectInput,
  required,
  FormDataConsumer,
  ArrayInput,
  SimpleFormIterator,
  useInput,
} from 'react-admin';
import { useFormContext } from 'react-hook-form';
import {
  Card,
  Typography,
  Box,
  Divider,
  Autocomplete,
  TextField,
  CircularProgress,
  Grid,
} from '@mui/material';
import {
  TaskDataSourceType,
  TaskFunctionType,
  CsvFilterOperator,
} from '@/api/gen/model';
import InstantExpressionGenerator from '@/components/InstantExpressionGenerator';
import {
  useGetApiPlants,
  useGetApiCsvSchemasPlantPlantId,
  useGetApiCsvSchemasId,
} from '@/api/gen/api';
import GeneralVariableSelectorRefactor from '@/components/AutocompleteSelector/GeneralVariableSelectorRefactor';

const validateScadaReferenceName = [
  required('La referencia SCADA es obligatoria'),
];
const validatePlantId = [required('La planta es obligatoria')];
const validateDataSourceType = [
  required('El tipo de fuente de datos es obligatorio'),
];

// Validación condicional para campos instant
const validateInstantExpression = (value: any, allValues: any) => {
  if (allValues.instantEnabled && !value) {
    return 'La expresión instantánea es obligatoria cuando instant está habilitado';
  }
  return undefined;
};

const validateInstantCalculationType = (value: any, allValues: any) => {
  if (allValues.instantEnabled && !value) {
    return 'El tipo de cálculo es obligatorio cuando instant está habilitado';
  }
  return undefined;
};

// Validación condicional para dataSource
const validateVariableId = (value: any) => {
  if (value === TaskDataSourceType.VARIABLE && !value) {
    return 'El ID de variable es obligatorio para tipo VARIABLE';
  }
  return undefined;
};

const validateCsvColumnId = (value: any, allValues: any) => {
  if (
    allValues.dataSource?.dataSourceType === TaskDataSourceType.CSV_COLUMN &&
    !value
  ) {
    return 'El ID de columna CSV es obligatorio para tipo CSV_COLUMN';
  }
  return undefined;
};

// Componente interno para manejar la selección de columnas CSV
const CsvColumnSelectorContent = ({
  formData,
  plants,
  schemas,
  columns,
  doubleColumns,
  selectedPlantId,
  setSelectedPlantId,
  selectedSchemaId,
  setSelectedSchemaId,
}: any) => {
  const { setValue } = useFormContext();

  return (
    <>
      <Box sx={{ mb: 2 }}>
        <Autocomplete
          options={plants}
          getOptionLabel={(option: any) => option.plantName || ''}
          value={plants.find((p: any) => p.id === selectedPlantId) || null}
          onChange={(_, newValue) => {
            setSelectedPlantId(newValue?.id);
            setSelectedSchemaId(undefined);
            setValue('dataSource.csvColumnId', undefined);
            setValue('dataSource.csvFilters', []);
          }}
          renderInput={params => (
            <TextField
              {...params}
              label='Planta'
              required
              helperText='Seleccione la planta del esquema CSV'
              size='medium'
            />
          )}
          fullWidth
          size='medium'
        />
      </Box>

      {selectedPlantId && (
        <Box sx={{ mb: 2 }}>
          <Autocomplete
            options={schemas}
            getOptionLabel={(option: any) => option.name || ''}
            value={schemas.find((s: any) => s.id === selectedSchemaId) || null}
            onChange={(_, newValue) => {
              setSelectedSchemaId(newValue?.id);
              setValue('dataSource.csvColumnId', undefined);
              setValue('dataSource.csvFilters', []);
            }}
            renderInput={params => (
              <TextField
                {...params}
                label='Esquema CSV'
                required
                helperText='Seleccione el esquema CSV'
                size='medium'
              />
            )}
            fullWidth
            size='medium'
          />
        </Box>
      )}

      {selectedSchemaId && doubleColumns.length > 0 && (
        <>
          <Box sx={{ mb: 2 }}>
            <Autocomplete
              options={doubleColumns}
              getOptionLabel={(option: any) =>
                `${option.header} (${option.dataType})` || ''
              }
              value={
                doubleColumns.find(
                  (c: any) => c.id === formData.dataSource?.csvColumnId
                ) || null
              }
              onChange={(_, newValue) => {
                setValue('dataSource.csvColumnId', newValue?.id);
              }}
              renderInput={params => (
                <TextField
                  {...params}
                  label='Columna CSV (Solo tipo Double)'
                  required
                  helperText='Seleccione la columna CSV de tipo Double para la suscripción'
                  size='medium'
                />
              )}
              fullWidth
              size='medium'
            />
          </Box>

          <Box sx={{ mt: 2 }}>
            <Typography variant='subtitle2' gutterBottom>
              Filtros CSV (Opcional)
            </Typography>
            <ArrayInput source='dataSource.csvFilters' label=''>
              <SimpleFormIterator inline>
                <SelectInput
                  source='csvColunmId'
                  label='Columna Filtro'
                  choices={columns.map((c: any) => ({
                    id: c.id,
                    name: `${c.header} (${c.dataType})`,
                  }))}
                  helperText='Columna para filtrar'
                />
                <SelectInput
                  source='csvFilterOperator'
                  label='Operador'
                  choices={[
                    {
                      id: CsvFilterOperator.Equal,
                      name: 'Igual (=)',
                    },
                    {
                      id: CsvFilterOperator.NotEqual,
                      name: 'No Igual (≠)',
                    },
                    {
                      id: CsvFilterOperator.GreaterThan,
                      name: 'Mayor que (>)',
                    },
                    {
                      id: CsvFilterOperator.GreaterThanOrEqual,
                      name: 'Mayor o Igual (≥)',
                    },
                    {
                      id: CsvFilterOperator.LessThan,
                      name: 'Menor que (<)',
                    },
                    {
                      id: CsvFilterOperator.LessThanOrEqual,
                      name: 'Menor o Igual (≤)',
                    },
                    {
                      id: CsvFilterOperator.Contains,
                      name: 'Contiene',
                    },
                    {
                      id: CsvFilterOperator.StartsWith,
                      name: 'Empieza con',
                    },
                    {
                      id: CsvFilterOperator.EndsWith,
                      name: 'Termina con',
                    },
                  ]}
                />
                <TextInput
                  source='value'
                  label='Valor'
                  helperText='Valor a comparar'
                />
              </SimpleFormIterator>
            </ArrayInput>
          </Box>
        </>
      )}

      {selectedSchemaId && columns.length > 0 && doubleColumns.length === 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography color='error' variant='body2'>
            ⚠️ El esquema seleccionado no tiene columnas de tipo Double. Por
            favor, seleccione otro esquema.
          </Typography>
        </Box>
      )}
    </>
  );
};

// Componente para manejar InstantExpressionGenerator
const InstantExpressionWrapper = () => {
  const { setValue } = useFormContext();

  return (
    <InstantExpressionGenerator
      onChange={value => {
        setValue('instantExpression', value);
      }}
    />
  );
};

// Componente para manejar GeneralVariableSelectorRefactor
const VariableSelectorWrapper = () => {
  const { setValue } = useFormContext();

  return (
    <GeneralVariableSelectorRefactor
      onVariableSelect={(variableId, variableName, variableObj) => {
        setValue('dataSource.variableId', variableId);
      }}
      showPaper={false}
      showHeader={true}
    />
  );
};

// Componente para el selector de planta en información general
const PlantSelectorWrapper = ({ plants }: any) => {
  const { setValue, watch } = useFormContext();
  const currentPlantId = watch('plantId');

  return (
    <Box sx={{ mb: 1 }}>
      <Autocomplete
        options={plants}
        getOptionLabel={(option: any) => option.plantName || ''}
        value={plants.find((p: any) => p.id === currentPlantId) || null}
        onChange={(_, newValue) => {
          setValue('plantId', newValue?.id || null);
        }}
        renderInput={params => (
          <TextField
            {...params}
            label='Planta'
            required
            helperText='Seleccione la planta asociada a la suscripción'
            size='medium'
            margin='dense'
          />
        )}
        fullWidth
        size='medium'
      />
    </Box>
  );
};

export default function SubscriptionCreate() {
  const [selectedPlantId, setSelectedPlantId] = useState<number | undefined>();
  const [selectedSchemaId, setSelectedSchemaId] = useState<
    number | undefined
  >();

  const { data: plantsData } = useGetApiPlants();
  const { data: schemasData } = useGetApiCsvSchemasPlantPlantId(
    selectedPlantId ?? 0,
    {
      query: {
        enabled: !!selectedPlantId,
      },
    }
  );
  const { data: schemaDetail } = useGetApiCsvSchemasId(selectedSchemaId ?? 0, {
    query: {
      enabled: !!selectedSchemaId,
    },
  });

  const plants = (plantsData as any)?.items || (plantsData as any) || [];
  const schemas = schemasData || [];
  const columns = schemaDetail?.columns || [];
  const doubleColumns = columns.filter((c: any) => c.dataType === 'Double');

  // Transformar los datos antes de enviarlos al backend
  const transform = (data: any) => {
    const transformed = { ...data };

    // Si hay csvFilters, normalizar las propiedades para el backend
    if (transformed.dataSource?.csvFilters) {
      transformed.dataSource.csvFilters = transformed.dataSource.csvFilters.map(
        (filter: any) => ({
          // El backend espera csvColumnId (no csvColunmId)
          csvColumnId: filter.csvColunmId || filter.csvColumnId,
          // El backend espera operator (no csvFilterOperator)
          operator: filter.csvFilterOperator || filter.operator,
          value: filter.value,
        })
      );
    }

    return transformed;
  };

  return (
    <Create redirect='list' transform={transform}>
      <SimpleForm>
        <Card sx={{ p: 3, mb: 2, width: '100%' }}>
          <Typography variant='h6' gutterBottom>
            Información General
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <Box sx={{ mb: 1 }}>
            <TextInput
              source='scadaReferenceName'
              label='Referencia SCADA'
              validate={validateScadaReferenceName}
              fullWidth
              helperText='Nombre de referencia en SCADA (máx. 200 caracteres)'
            />
          </Box>

          <PlantSelectorWrapper plants={plants} />

          <Box sx={{ mb: 1 }}>
            <TextInput
              source='unitChange'
              label='Cambio de Unidad'
              fullWidth
              helperText='Opcional: Ej. /1000 (máx. 50 caracteres)'
            />
          </Box>
        </Card>

        <Card sx={{ p: 3, mb: 2, width: '100%' }}>
          <Typography variant='h6' gutterBottom>
            Configuración Instantánea
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <BooleanInput
            source='instantEnabled'
            label='Habilitar Instantáneo'
            defaultValue={false}
          />

          <FormDataConsumer>
            {({ formData }) =>
              formData.instantEnabled && (
                <Grid container spacing={2} alignItems='flex-end'>
                  <Grid item xs={12} md={5.5}>
                    <InstantExpressionWrapper />
                  </Grid>
                  <Grid
                    item
                    md={1}
                    sx={{
                      display: { xs: 'none', md: 'flex' },
                      justifyContent: 'center',
                      alignItems: 'stretch',
                    }}
                  >
                    <Divider orientation='vertical' flexItem />
                  </Grid>
                  <Grid item xs={12} md={5.5}>
                    <Box sx={{ width: '100%' }}>
                      <SelectInput
                        source='instantCalculationType'
                        label='Tipo de Cálculo'
                        validate={validateInstantCalculationType}
                        choices={[
                          { id: TaskFunctionType.AVG, name: 'Promedio' },
                          { id: TaskFunctionType.SUM, name: 'Suma' },
                          { id: TaskFunctionType.MAX, name: 'Máximo' },
                          { id: TaskFunctionType.MIN, name: 'Mínimo' },
                          { id: TaskFunctionType.TOTALIZED, name: 'Totalizado' },
                          { id: TaskFunctionType.CUMULATIVE, name: 'Acumulado' },
                        ]}
                        fullWidth
                        sx={{ width: '100%' }}
                      />
                    </Box>
                  </Grid>
                </Grid>
              )
            }
          </FormDataConsumer>
        </Card>

        <Card sx={{ p: 3, mb: 2, width: '100%' }}>
          <Typography variant='h6' gutterBottom>
            Fuente de Datos
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <Box sx={{ width: '100%' }}>
            <SelectInput
              source='dataSource.dataSourceType'
              label='Tipo de Fuente de Datos'
              validate={validateDataSourceType}
              choices={[
                { id: TaskDataSourceType.VARIABLE, name: 'Variable' },
                { id: TaskDataSourceType.CSV_COLUMN, name: 'Columna CSV' },
              ]}
              fullWidth
              sx={{ width: '100%' }}
            />
          </Box>

          <FormDataConsumer>
            {({ formData }) => {
              const dataSourceType = formData.dataSource?.dataSourceType;

              if (dataSourceType === TaskDataSourceType.VARIABLE) {
                return <VariableSelectorWrapper />;
              }

              if (dataSourceType === TaskDataSourceType.CSV_COLUMN) {
                return (
                  <CsvColumnSelectorContent
                    formData={formData}
                    plants={plants}
                    schemas={schemas}
                    columns={columns}
                    doubleColumns={doubleColumns}
                    selectedPlantId={selectedPlantId}
                    setSelectedPlantId={setSelectedPlantId}
                    selectedSchemaId={selectedSchemaId}
                    setSelectedSchemaId={setSelectedSchemaId}
                  />
                );
              }

              return null;
            }}
          </FormDataConsumer>
        </Card>
      </SimpleForm>
    </Create>
  );
}
